/**
 * Test Booking Form
 * Form for creating test bookings with minimal required fields
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Button } from '@/components/admin/ui/button'
import { Input } from '@/components/admin/ui/input'
import { Label } from '@/components/admin/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/admin/ui/select'
import { useToast } from '@/hooks/use-toast'
import { testBookingService } from '@/services/testBookingService'
import { Loader2, Plus } from 'lucide-react'
import { format, addDays } from 'date-fns'
import CityAutocomplete from '@/components/ui/AddressAutocomplete'
import { DatePicker } from '@/components/photographers/DatePicker'
import { supabaseAdmin } from '@/integrations/supabase/client'
import {
  getBasePhotoPrice,
  getVideoOnlyPrice,
  getPhotoVideoPrice,
  formatPrice
} from '@/lib/constants/packagePricing'
import { ADD_ONS_CONFIG } from '@/data/addOnsConfig'
import { Ticket } from 'lucide-react'

interface Promotion {
  id: string
  code: string
  discount_type: 'money_off' | 'percentage_off' | 'addon_free'
  discount_value: number | null
  free_addons: string[] | null  // Database field name (not addon_ids)
  is_active: boolean
}

interface TestBookingFormProps {
  onBookingCreated: () => void
}

// Test account IDs - add more here as needed
const TEST_CUSTOMER_IDS = ['dceb58d3-0f84-4878-92a1-bedeae4fddc9']
const TEST_PHOTOGRAPHER_IDS = ['de5de0c1-6a31-403c-b318-3be66cb68a9a']

interface TestAccount {
  id: string
  label: string
}

export const TestBookingForm = ({ onBookingCreated }: TestBookingFormProps) => {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [loadingAccounts, setLoadingAccounts] = useState(true)

  // Test accounts with fetched names/emails
  const [testCustomers, setTestCustomers] = useState<TestAccount[]>([])
  const [testPhotographers, setTestPhotographers] = useState<TestAccount[]>([])
  const [promotions, setPromotions] = useState<Promotion[]>([])

  // Form state
  const [customerId, setCustomerId] = useState('')
  const [photographerId, setPhotographerId] = useState('')
  const [selectedCoupon, setSelectedCoupon] = useState<string>('') // coupon code

  const loadTestAccounts = async () => {
    setLoadingAccounts(true)
    try {
      // Fetch customer info
      const { data: customers } = await supabaseAdmin
        .from('users')
        .select('id, full_name, email')
        .in('id', TEST_CUSTOMER_IDS)

      // Fetch photographer info
      const { data: photographers } = await supabaseAdmin
        .from('photographers')
        .select('id, user:users!photographers_user_id_fkey(full_name, email)')
        .in('id', TEST_PHOTOGRAPHER_IDS)

      setTestCustomers(
        (customers || []).map(c => ({
          id: c.id,
          label: `${c.full_name || 'Unknown'} - ${c.email}`
        }))
      )

      setTestPhotographers(
        (photographers || []).map(p => ({
          id: p.id,
          label: `${p.user?.full_name || 'Unknown'} - ${p.user?.email || 'No email'}`
        }))
      )
    } catch (error) {
      console.error('Failed to load test accounts:', error)
    } finally {
      setLoadingAccounts(false)
    }
  }

  const loadPromotions = async () => {
    try {
      // Cast to any since 'promotions' table isn't in generated TypeScript schema
      const { data, error } = await (supabaseAdmin as any)
        .from('promotions')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching promotions:', error)
        throw error
      }

      console.log('Loaded promotions:', data)
      setPromotions((data || []) as Promotion[])
    } catch (error) {
      console.error('Failed to load promotions:', error)
    }
  }

  useEffect(() => {
    loadTestAccounts()
    loadPromotions()
  }, [])

  const [eventDate, setEventDate] = useState(format(addDays(new Date(), 14), 'yyyy-MM-dd'))
  const [eventTime, setEventTime] = useState('14:00')
  const [packageType, setPackageType] = useState('photo')
  const [hoursBooked, setHoursBooked] = useState('4')
  const [location, setLocation] = useState('') // Display value: "City, State"
  const [locationCity, setLocationCity] = useState('') // Parsed city for DB
  const [locationState, setLocationState] = useState('') // Parsed state for DB
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([])
  // Live catalog rows keyed by code. Promo add-ons such as Hour on Us exist only in the addons
  // table, not in ADD_ONS_CONFIG, so names/prices for those come from here.
  const [dbAddons, setDbAddons] = useState<Record<string, { name: string; price: number }>>({})

  useEffect(() => {
    const loadDbAddons = async () => {
      const { data } = await (supabaseAdmin as any)
        .from('addons')
        .select('code, name, price_cents')
        .eq('is_active', true)
        .not('code', 'is', null)
      const map: Record<string, { name: string; price: number }> = {}
      for (const row of data || []) map[row.code] = { name: row.name, price: Number(row.price_cents || 0) / 100 }
      setDbAddons(map)
    }
    loadDbAddons()
  }, [])

  // Display name for a selected add-on: catalog title, then addons-table name, then the raw code
  const getAddOnTitle = (addOnId: string): string =>
    ADD_ONS_CONFIG.find(a => a.id === addOnId)?.title || dbAddons[addOnId]?.name || addOnId

  // Calculate add-on price
  const getAddOnPrice = (addOnId: string): number => {
    const addOn = ADD_ONS_CONFIG.find(a => a.id === addOnId)
    if (!addOn) return dbAddons[addOnId]?.price ?? 0

    // Handle dynamic pricing for video-coverage
    if (addOnId === 'video-coverage' && addOn.pricing?.priceByHours) {
      const hours = parseInt(hoursBooked)
      return addOn.pricing.priceByHours[hours] || addOn.basePrice
    }

    // Handle dynamic pricing for second-shooter
    if (addOnId === 'second-shooter' && addOn.pricing?.isDynamic) {
      const hours = Math.max(parseInt(hoursBooked), addOn.pricing.minimumHours || 4)
      return hours * (addOn.pricing.photoOnlyRate || 150)
    }

    return addOn.basePrice
  }

  // Get selected promotion details
  const selectedPromotion = useMemo(() => {
    if (!selectedCoupon) return null
    return promotions.find(p => p.code === selectedCoupon) || null
  }, [selectedCoupon, promotions])

  // Auto-select free add-ons when addon_free coupon is selected (like Express Checkout)
  useEffect(() => {
    if (selectedPromotion?.discount_type === 'addon_free' && selectedPromotion.free_addons) {
      const freeAddonIds = selectedPromotion.free_addons
      setSelectedAddOns(prev => {
        // Add any free add-ons that aren't already selected
        const newAddons = [...prev]
        freeAddonIds.forEach(addonId => {
          if (!newAddons.includes(addonId)) {
            newAddons.push(addonId)
          }
        })
        return newAddons
      })
    }
  }, [selectedPromotion])

  // Calculate total amount based on package type, hours, add-ons, and coupon
  const { baseAmount, addOnsAmount, discountAmount, totalAmount, freeAddOns } = useMemo(() => {
    const hours = parseInt(hoursBooked)
    let base = 0

    switch (packageType) {
      case 'photo':
        base = getBasePhotoPrice(hours) || 0
        break
      case 'photoVideo':
        base = getPhotoVideoPrice(hours) || 0
        break
      case 'videoOnly':
        base = getVideoOnlyPrice(hours) || 0
        break
      case 'engagement':
        base = getBasePhotoPrice(hours) || 0
        break
    }

    // Calculate add-ons, accounting for free add-ons from coupon
    let freeAddonIds: string[] = []
    if (selectedPromotion?.discount_type === 'addon_free' && selectedPromotion.free_addons) {
      freeAddonIds = selectedPromotion.free_addons
    }

    const addOns = selectedAddOns.reduce((sum, id) => {
      if (freeAddonIds.includes(id)) return sum // Free add-on
      return sum + getAddOnPrice(id)
    }, 0)

    const subtotal = base + addOns

    // Calculate discount
    let discount = 0
    if (selectedPromotion) {
      if (selectedPromotion.discount_type === 'money_off' && selectedPromotion.discount_value) {
        discount = selectedPromotion.discount_value
      } else if (selectedPromotion.discount_type === 'percentage_off' && selectedPromotion.discount_value) {
        discount = Math.round(subtotal * (selectedPromotion.discount_value / 100))
      }
      // addon_free is handled above by excluding from add-on calculation
    }

    return {
      baseAmount: base,
      addOnsAmount: addOns,
      discountAmount: discount,
      totalAmount: Math.max(0, subtotal - discount),
      freeAddOns: freeAddonIds
    }
  }, [packageType, hoursBooked, selectedAddOns, selectedPromotion])

  const toggleAddOn = useCallback((addOnId: string) => {
    setSelectedAddOns(prev =>
      prev.includes(addOnId)
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    )
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!customerId) {
      toast({
        title: 'Validation Error',
        description: 'Please select a customer',
        variant: 'destructive'
      })
      return
    }

    if (!photographerId) {
      toast({
        title: 'Validation Error',
        description: 'Please select a photographer',
        variant: 'destructive'
      })
      return
    }

    if (!locationCity || !locationState) {
      toast({
        title: 'Validation Error',
        description: 'Please select an event location',
        variant: 'destructive'
      })
      return
    }

    setLoading(true)
    try {
      await testBookingService.createTestBooking({
        customer_id: customerId,
        photographer_id: photographerId,
        event_date: eventDate,
        event_time: eventTime,
        package_type: packageType,
        event_type: 'test_wedding',
        hours_booked: parseInt(hoursBooked),
        total_amount: totalAmount,
        location_city: locationCity,
        location_state: locationState,
        add_ons: selectedAddOns,
        promotionDetails: selectedPromotion ? {
          id: selectedPromotion.id,
          code: selectedPromotion.code,
          discount_type: selectedPromotion.discount_type,
          discount_value: selectedPromotion.discount_value,
          free_addons: selectedPromotion.free_addons || []
        } : undefined
      })

      toast({
        title: 'Success',
        description: 'Test booking created successfully'
      })

      // Reset form
      setEventDate(format(addDays(new Date(), 14), 'yyyy-MM-dd'))
      setLocation('')
      setLocationCity('')
      setLocationState('')
      setSelectedAddOns([])
      setSelectedCoupon('')

      onBookingCreated()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create test booking',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  if (loadingAccounts) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading test accounts...</span>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Customer Selection */}
        <div className="space-y-2">
          <Label htmlFor="customer">Customer *</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger id="customer">
              <SelectValue placeholder="Select customer..." />
            </SelectTrigger>
            <SelectContent>
              {testCustomers.map(customer => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Photographer Selection */}
        <div className="space-y-2">
          <Label htmlFor="photographer">Photographer *</Label>
          <Select value={photographerId} onValueChange={setPhotographerId}>
            <SelectTrigger id="photographer">
              <SelectValue placeholder="Select photographer..." />
            </SelectTrigger>
            <SelectContent>
              {testPhotographers.map(photographer => (
                <SelectItem key={photographer.id} value={photographer.id}>
                  {photographer.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Event Date */}
        <div className="space-y-2">
          <Label>Event Date *</Label>
          <DatePicker
            value={eventDate}
            onChange={(isoDate) => setEventDate(isoDate || format(addDays(new Date(), 14), 'yyyy-MM-dd'))}
            placeholder="Select event date"
            minDate={new Date()}
            className="w-full"
          />
        </div>

        {/* Event Time */}
        <div className="space-y-2">
          <Label htmlFor="eventTime">Event Time</Label>
          <Input
            id="eventTime"
            type="time"
            value={eventTime}
            onChange={(e) => setEventTime(e.target.value)}
          />
        </div>

        {/* Package Type */}
        <div className="space-y-2">
          <Label htmlFor="packageType">Package Type</Label>
          <Select value={packageType} onValueChange={setPackageType}>
            <SelectTrigger id="packageType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="photo">Photo Only</SelectItem>
              <SelectItem value="photoVideo">Photo + Video</SelectItem>
              <SelectItem value="videoOnly">Video Only</SelectItem>
              <SelectItem value="engagement">Engagement Session</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Hours Booked */}
        <div className="space-y-2">
          <Label htmlFor="hoursBooked">Hours Booked</Label>
          <Select value={hoursBooked} onValueChange={setHoursBooked}>
            <SelectTrigger id="hoursBooked">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map(hours => (
                <SelectItem key={hours} value={hours.toString()}>
                  {hours} hour{hours > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Add-Ons */}
        <div className="space-y-3 md:col-span-2">
          <Label>Add-Ons (Optional)</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ADD_ONS_CONFIG.map(addOn => {
              const isSelected = selectedAddOns.includes(addOn.id)
              const addOnPrice = getAddOnPrice(addOn.id)
              const isFreeFromCoupon = freeAddOns.includes(addOn.id)
              return (
                <div
                  key={addOn.id}
                  role="button"
                  tabIndex={0}
                  className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? isFreeFromCoupon
                        ? 'bg-green-100 border-green-500 dark:bg-green-950 dark:border-green-600'
                        : 'bg-primary/10 border-primary'
                      : 'bg-muted/30 border-border hover:bg-muted/50'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    toggleAddOn(addOn.id)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                      e.preventDefault()
                      toggleAddOn(addOn.id)
                    }
                  }}
                >
                  <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                    isSelected ? 'bg-primary border-primary' : 'border-input'
                  }`}>
                    {isSelected && (
                      <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{addOn.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {isFreeFromCoupon && isSelected ? (
                        <span className="text-green-600 font-medium">FREE with coupon</span>
                      ) : (
                        formatPrice(addOnPrice)
                      )}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Coupon Code */}
        <div className="space-y-2 md:col-span-2">
          <Label className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            Apply Coupon (Optional)
          </Label>
          <Select value={selectedCoupon || 'none'} onValueChange={(val) => setSelectedCoupon(val === 'none' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a coupon..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No coupon</SelectItem>
              {promotions.map(promo => (
                <SelectItem key={promo.id} value={promo.code}>
                  <span className="font-mono">{promo.code}</span>
                  <span className="ml-2 text-muted-foreground">
                    {promo.discount_type === 'money_off' && `$${promo.discount_value} off`}
                    {promo.discount_type === 'percentage_off' && `${promo.discount_value}% off`}
                    {promo.discount_type === 'addon_free' && `Free add-ons`}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPromotion && (
            <div className="text-sm text-green-600 dark:text-green-400">
              <p>✓ Coupon &ldquo;{selectedPromotion.code}&rdquo; applied</p>
              {selectedPromotion.discount_type === 'addon_free' && selectedPromotion.free_addons && (
                <p className="text-xs mt-1">
                  Free add-ons auto-selected: {selectedPromotion.free_addons.map(id => {
                    const addon = ADD_ONS_CONFIG.find(a => a.id === id)
                    return addon?.title || id
                  }).join(', ')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Total Amount (calculated) */}
        <div className="space-y-2 md:col-span-2">
          <Label>Total Amount</Label>
          <div className="p-3 rounded-md border border-input bg-muted">
            <div className="flex justify-between text-sm">
              <span>Package ({packageType})</span>
              <span>{formatPrice(baseAmount)}</span>
            </div>
            {/* Paid add-ons, one line each; promo freebies listed separately so nothing looks double-counted */}
            {selectedAddOns.filter(id => !freeAddOns.includes(id)).map(id => (
              <div key={id} className="flex justify-between text-sm text-muted-foreground">
                <span>Add-on: {getAddOnTitle(id)}</span>
                <span>+{formatPrice(getAddOnPrice(id))}</span>
              </div>
            ))}
            {selectedAddOns.filter(id => freeAddOns.includes(id)).map(id => (
              <div key={id} className="flex justify-between text-sm text-green-600">
                <span>Promo {selectedPromotion?.code}: {getAddOnTitle(id)}</span>
                <span>
                  {getAddOnPrice(id) > 0 && (
                    <span className="line-through text-muted-foreground mr-2">{formatPrice(getAddOnPrice(id))}</span>
                  )}
                  FREE
                </span>
              </div>
            ))}
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Coupon discount</span>
                <span>-{formatPrice(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium text-base mt-2 pt-2 border-t">
              <span>Total</span>
              <span>{formatPrice(totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Event Location */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="location">Event Location *</Label>
          <CityAutocomplete
            value={location}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocation(e.target.value)}
            onCitySelect={(cityData: { city: string; state: string }) => {
              setLocation(`${cityData.city}, ${cityData.state}`)
              setLocationCity(cityData.city)
              setLocationState(cityData.state)
            }}
            placeholder="Start typing city name..."
            id="location"
            name="location"
            required
          />
          {locationCity && locationState && (
            <p className="text-sm text-muted-foreground">
              Selected: {locationCity}, {locationState}
            </p>
          )}
        </div>
      </div>

      <Button type="submit" disabled={loading} className="w-full md:w-auto">
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <Plus className="mr-2 h-4 w-4" />
            Create Test Booking
          </>
        )}
      </Button>
    </form>
  )
}
